import React, { useState, useEffect, useMemo } from 'react';
import apiFetch from '@wordpress/api-fetch';

const initials = (name = '') => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatPrice = (value, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style:                 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${parseFloat(value).toFixed(2)}`;
  }
};

const PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

const DonationReportTab = () => {
  const [orders, setOrders]     = useState([]);
  const [currency, setCurrency] = useState('USD');
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState('');
  const [sortDir, setSortDir]   = useState('desc');
  const [currentPage, setPage]  = useState(1);
  const [perPage, setPerPage]   = useState(5);

  useEffect(() => {
    apiFetch({ path: `/dotfw/v1/donation-report?nocache=${Date.now()}` })
      .then((res) => {
        setOrders(res.orders || []);
        setCurrency(res.currency || 'USD');
      })
      .catch((err) => {
        console.error('Error fetching donation report:', err);
        setError('Failed to load donation data. Please refresh and try again.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPage(1); }, [search, perPage]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let list = q
      ? orders.filter(
          (o) =>
            String(o.order_id).includes(q) ||
            o.customer.toLowerCase().includes(q)
        )
      : [...orders];
    list.sort((a, b) =>
      sortDir === 'asc'
        ? parseFloat(a.value) - parseFloat(b.value)
        : parseFloat(b.value) - parseFloat(a.value)
    );
    return list;
  }, [orders, search, sortDir]);

  const totalAmount = useMemo(
    () => filtered.reduce((sum, o) => sum + parseFloat(o.value || 0), 0),
    [filtered]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage   = Math.min(currentPage, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, safePage, perPage]);

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const delta = 2;
    const left  = Math.max(2, safePage - delta);
    const right = Math.min(totalPages - 1, safePage + delta);
    const range = [1];
    if (left > 2) range.push('...');
    for (let i = left; i <= right; i++) range.push(i);
    if (right < totalPages - 1) range.push('...');
    range.push(totalPages);
    return range;
  }, [totalPages, safePage]);

  const startEntry = filtered.length === 0 ? 0 : (safePage - 1) * perPage + 1;
  const endEntry   = Math.min(safePage * perPage, filtered.length);

  return (
    <div className="tab-root">

      {/* ── Section card ── */}
      <div className="tab-section">

        {/* ── Section header ── */}
        <div className="tab-section-header">
          <div className="tab-section-meta" style={{ flex: 1 }}>
            <h3 className="tab-section-title">Donation Report</h3>
            <p className="tab-section-sub">All orders containing a donation or tip</p>
          </div>

          {/* Summary pills — shown once data is ready */}
          {!loading && !error && (
            <div className="dotfw-report-meta">
              <span className="dotfw-report-pill dotfw-report-pill--green">
                {formatPrice(totalAmount, currency)} total
              </span>
              <span className="dotfw-report-pill">
                {filtered.length} Donation{filtered.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* ── Toolbar ── */}
        {!loading && !error && orders.length > 0 && (
          <div className="dotfw-report-toolbar">
            <div className="dotfw-report-search-wrap">
              <input
                type="search"
                className="dotfw-report-search"
                placeholder="Search customer or order ID…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="dotfw-report-toolbar-right">
              <label className="dotfw-perpage-label" htmlFor="dotfw-perpage">
                Rows per page
              </label>
              <select
                id="dotfw-perpage"
                className="dotfw-perpage-select"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ── Loading state ── */}
        {loading && (
          <div className="dotfw-report-state">
            <span className="rotating-spinner" aria-hidden="true" />
            <span>Loading donations…</span>
          </div>
        )}

        {/* ── Error state ── */}
        {error && !loading && (
          <div className="dotfw-report-state dotfw-report-state--error">
            {error}
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && !error && orders.length === 0 && (
          <div className="dotfw-report-state">
            <div className="dotfw-report-empty-icon">📭</div>
            <p>No donations found.</p>
            <span className="dotfw-report-empty-sub">
              Donations will appear here once customers add them at checkout.
            </span>
          </div>
        )}

        {/* ── Table + Pagination ── */}
        {!loading && !error && orders.length > 0 && (
          <>
            <div className="dotfw-report-table-wrap">
              <table className="dotfw-report-table">
                <colgroup>
                  <col style={{ width: '50px'  }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th
                      className="dotfw-th-right dotfw-th-sortable"
                      onClick={toggleSort}
                      title="Sort by value"
                    >
                      Value {sortDir === 'asc' ? '↑' : '↓'}
                    </th>
                    <th>Date / Time</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="dotfw-td-empty">
                        No results for "{search}"
                      </td>
                    </tr>
                  ) : (
                    paginated.map((order, idx) => (
                      <tr key={order.id}>
                        <td className="dotfw-td-muted">
                          {(safePage - 1) * perPage + idx + 1}
                        </td>
                        <td>
                          <a
                            href={order.edit_url}
                            target="_blank"
                            rel="noreferrer"
                            className="dotfw-order-link"
                          >
                            #{order.order_id}
                            <span className="dotfw-order-link-icon">↗</span>
                          </a>
                        </td>
                        <td>
                          <div className="dotfw-customer-cell">
                            <div className="dotfw-avatar">
                              {initials(order.customer)}
                            </div>
                            {order.customer || '—'}
                          </div>
                        </td>
                        <td className="dotfw-td-right">
                          <span className="dotfw-value-badge">
                            {formatPrice(order.value, currency)}
                          </span>
                        </td>
                        <td className="dotfw-td-muted dotfw-td-date">
                          {order.date}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {paginated.length > 0 && (
                  <tfoot>
                    <tr className="dotfw-total-row">
                      <td colSpan={3} className="dotfw-total-label">
                        Page total
                      </td>
                      <td className="dotfw-td-right">
                        <span className="dotfw-value-badge">
                          {formatPrice(totalAmount, currency)}
                        </span>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* ── Pagination bar ── */}
            {totalPages > 1 && (
              <div className="dotfw-pagination">
                <span className="dotfw-pagination-info">
                  {startEntry}–{endEntry} of {filtered.length}
                </span>

                <div className="dotfw-pagination-controls">
                  <button
                    className="dotfw-page-btn"
                    onClick={() => setPage(1)}
                    disabled={safePage === 1}
                    aria-label="First page"
                  >«</button>

                  <button
                    className="dotfw-page-btn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    aria-label="Previous page"
                  >‹</button>

                  {pageNumbers.map((p, i) =>
                    p === '...' ? (
                      <span key={`el-${i}`} className="dotfw-page-ellipsis">…</span>
                    ) : (
                      <button
                        key={p}
                        className={`dotfw-page-btn${safePage === p ? ' dotfw-page-btn--active' : ''}`}
                        onClick={() => setPage(p)}
                        aria-current={safePage === p ? 'page' : undefined}
                      >{p}</button>
                    )
                  )}

                  <button
                    className="dotfw-page-btn"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    aria-label="Next page"
                  >›</button>

                  <button
                    className="dotfw-page-btn"
                    onClick={() => setPage(totalPages)}
                    disabled={safePage === totalPages}
                    aria-label="Last page"
                  >»</button>
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default DonationReportTab;